import React, { useEffect, useState, useRef, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./components/AuthContext.jsx";
import { SocketProvider } from "./components/Context.jsx";
import { BACKEND_URL as GREENWICH_BACKEND_URL } from "./utils/network.js";
import "./components/regOrg/reg.css";
import "./components/community/community.css";
import Navbar from "./components/Navbar.jsx";
import ToastContainer from "./components/ToastContainer.jsx";
import NotificationToast from "./components/NotificationToast.jsx";
import HomePage from "./components/HomePage.jsx"; 
import Whoweare from "./components/Whoweare.jsx"; 
import Footer from "./components/footer.jsx"; 
import Registration from "./components/Registration.jsx";
import Registratioon from './components/registratioon.jsx';
import PlayerReg from './components/PlayerReg.jsx';
import PlayerInterest from './components/PlayerInterest.jsx';
import Reeeg from './components/regOrg/Reeeg.jsx';
import Donate from "./components/Donate.jsx";
// Lazy-loaded: large, feature-specific pages most visitors never open on a given visit.
const Buddy = lazy(() => import("./components/Buddy.jsx"));
import LevelsPage from "./components/LevelsPage.jsx";
import TasksPage from "./components/TasksPage.jsx";
const ProPage = lazy(() => import("./components/ProPage.jsx"));
const PaymentConfirmation = lazy(() => import("./components/PaymentConfirmation.jsx"));
const ShopPage = lazy(() => import("./components/ShopPage.jsx"));
const ShopHistoryPage = lazy(() => import("./components/ShopHistoryPage.jsx"));
const GreencoinsPage = lazy(() => import("./components/GreencoinsPage.jsx"));
// Admin pages are lazy-loaded: they're a large chunk of the bundle (17 components) that
// only admins ever visit, so regular users shouldn't have to download them upfront.
const AdminShop = lazy(() => import("./components/Admin/AdminShop.jsx"));
const AdminTasks = lazy(() => import("./components/Admin/AdminTasks.jsx"));
const OrganizationGreencoins = lazy(() => import("./components/OrganizationGreencoins.jsx"));
const OrganizationPanel = lazy(() => import("./components/OrganizationPanel.jsx"));
const AdminGreencoins = lazy(() => import("./components/Admin/AdminGreencoins.jsx"));
const AdminRefunds = lazy(() => import("./components/Admin/AdminRefunds.jsx"));
import ContactUs from './components/ContactUs.jsx';
import Terms from './components/Terms.jsx';
import Privacy from './components/Privacy.jsx';
import OrgRoute from './components/OrgRoute.jsx';
import OrgProgramSubmit from './components/OrgProgramSubmit.jsx';
const AdminProgramsReview = lazy(() => import('./components/AdminProgramsReview.jsx'));
const Dashboard = lazy(() => import('./components/Dashboard.jsx'));
import UserPublicProfile from "./components/UserPublicProfile.jsx";
import Activities from "./components/Activities.jsx";
import Settings from "./components/Settings.jsx";
import Messages from "./components/Messages.jsx";
import NotificationsPage from "./components/NotificationsPage.jsx";
import SearchPage from "./components/SearchPage.jsx";
import FollowButton from "./components/FollowButton.jsx";
import FollowRequestsPage from "./components/FollowRequestsPage.jsx";
import FollowSettings from "./components/FollowSettings.jsx";
import BlockedUsers from "./components/BlockedUsers.jsx";
import MutedUsers from "./components/MutedUsers.jsx";
import ActiveSessions from "./components/ActiveSessions.jsx";
import NotificationSettings from "./components/NotificationSettings.jsx";
import SecuritySettings from "./components/SecuritySettings.jsx";
import ForgotPassword from "./components/ForgotPassword.jsx";
import ResetPassword from "./components/ResetPassword.jsx";
import VerifyEmail from "./components/VerifyEmail.jsx";
import TeamInviteConfirm from "./components/TeamInviteConfirm.jsx";
import OrgMySubmissions from "./components/OrgMySubmissions.jsx";
import Reg from './components/regOrg/Reg.jsx';
import Regnext from './components/regOrg/Regnext.jsx';
import Reeg from './components/regOrg/Reeg.jsx';
const AdminDashboard = lazy(() => import('./components/Admin/AdminDashboard.jsx'));
const BuddyMentorApprovals = lazy(() => import('./components/Admin/BuddyMentorApprovals.jsx'));
const PendingApprovals = lazy(() => import('./components/Admin/PendingApprovals.jsx'));
const AllUsers = lazy(() => import('./components/Admin/AllUsers.jsx'));
const UserStats = lazy(() => import('./components/Admin/UserStats.jsx'));
const AdminPrograms = lazy(() => import('./components/Admin/AdminPrograms.jsx'));
const OrganizerApprovals = lazy(() => import('./components/Admin/OrganizerApprovals.jsx'));
const AdminAnalytics = lazy(() => import('./components/Admin/AdminAnalytics.jsx'));
const AdminUserSegments = lazy(() => import('./components/Admin/AdminUserSegments.jsx'));
const SecondaryAdmins = lazy(() => import('./components/Admin/SecondaryAdmins.jsx'));
const AdminReports = lazy(() => import('./components/Admin/AdminReports.jsx'));
const AdminActionLog = lazy(() => import('./components/Admin/AdminActionLog.jsx'));
const AdminInbox = lazy(() => import('./components/Admin/AdminInbox.jsx'));
const UserInvestigation = lazy(() => import('./components/Admin/UserInvestigation.jsx'));
import VerifiedOrganizations from './components/VerifiedOrganizations/VerifiedOrganizations.jsx';
const VerifiedOrganizationsPage = lazy(() => import('./components/Admin/VerifiedOrganizationsPage.jsx'));
import './components/Admin/admin.css';
import Programs from './components/Programs.jsx';
import Feed from './components/Feed.jsx';
import MessagesWidget from './components/MessagesWidget.jsx';
import OrganizationAccountProfile from "./components/OrganizationAccountProfile.jsx";
const OrganizationDashboard = lazy(() => import("./components/OrganizationDashboard.jsx"));
import OrganizationActivities from "./components/OrganizationActivities.jsx";
import OrganizationSettings from "./components/OrganizationSettings.jsx";
const Community = lazy(() => import("./components/community/Community.jsx"));
const MyCommunities = lazy(() => import("./components/community/MyCommunities.jsx"));
const GroupView = lazy(() => import("./components/community/GroupView.jsx"));
const GroupAdminPanel = lazy(() => import("./components/community/GroupAdminPanel.jsx"));
const GroupChat = lazy(() => import("./components/community/GroupChat.jsx"));
import SinglePostView from "./components/Singlepostview.jsx";
import OnboardingGuide from "./components/OnboardingGuide.jsx";
import CookieConsent from "./components/CookieConsent.jsx";
import "./styles/clay-motion.css";
import "./app.css";
import { trackPageView, trackPageDuration, trackPageLeave } from "./utils/analytics.js";

const isPrimaryAdmin = (user) => user?.role === 'admin';
const isApprovedSecondaryAdmin = (user) => user?.role === 'secondary_admin' && user?.secondaryAdminApproved === true;
const hasAdminPermission = (user, permission) => {
  if (isPrimaryAdmin(user)) return true;
  if (!isApprovedSecondaryAdmin(user)) return false;
  const permissions = Array.isArray(user?.adminPermissions) ? user.adminPermissions : [];
  return permissions.includes(permission);
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="loading" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div className="spinner"></div>
      </div>
    );
  }
  if (!user) return <Navigate to="/registration" replace />;
  if (!hasAdminPermission(user, 'programs_review')) return <Navigate to="/feed" replace />;
  return children;
};

const PrimaryAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading">Loading...</div>;
  if (!user) return <Navigate to="/registration" replace />;
  if (!isPrimaryAdmin(user)) return <Navigate to="/feed" replace />;
  return children;
};

const PermissionRoute = ({ children, permission }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading">Loading...</div>;
  if (!user) return <Navigate to="/registration" replace />;
  if (!hasAdminPermission(user, permission)) return <Navigate to="/feed" replace />;
  return children;
};

const EnvironmentCheck = () => {
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🌍 Development mode');
    }
  }, []);
  return null;
};

const AuthRedirect = () => {
  const { user, redirectPath, clearRedirect } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (redirectPath) {
      let targetPath = redirectPath;
      if (user && isPrimaryAdmin(user) && redirectPath === '/feed') {
        targetPath = '/admin/dashboard';
      } else if (user && isApprovedSecondaryAdmin(user) && redirectPath === '/feed') {
        targetPath = '/admin/programs';
      }
      navigate(targetPath);
      clearRedirect();
    }
  }, [redirectPath, navigate, clearRedirect, user]);
  return null;
};

const RoleBasedDashboard = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading">Loading...</div>;
  if (!user) return <Navigate to="/registration" replace />;
  if (isPrimaryAdmin(user)) return <Navigate to="/admin/dashboard" replace />;
  if (isApprovedSecondaryAdmin(user)) return <Navigate to="/admin/programs" replace />;
  return <Dashboard />;
};

const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading">Loading...</div>;
  if (!user) return <Navigate to="/registration" replace />;
  if (requireAdmin && !isPrimaryAdmin(user)) return <Navigate to="/" replace />;
  return children;
};

const AdminProtectedRoute = ({ children }) => (
  <ProtectedRoute requireAdmin={true}>{children}</ProtectedRoute>
);

// Inside the Android/iOS app shell there's no browser address bar for a visitor to
// have arrived at via a marketing link, so the landing page's job (pitch the site to
// a first-time web visitor, get them to register) doesn't apply — someone who already
// installed the app opens straight into using it. Detected via the global Capacitor
// injects at runtime rather than importing @capacitor/core, since the web build (this
// same source, also used unmodified by the Capacitor-wrapped app build) doesn't carry
// that dependency.
const isNativeApp = () => typeof window !== 'undefined' && !!window.Capacitor?.isNativePlatform?.();

const RoleBasedFeed = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading">Loading...</div>;
  if (user) return <Navigate to="/feed" replace />;
  if (isNativeApp()) return <Navigate to="/programs" replace />;
  return <HomePage />;
};

const OrganizationProfileRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/profile/${id}`} replace />;
};

const RoleBasedRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading">Loading...</div>;
  if (!user) return <Navigate to="/registration" replace />;
  if (isPrimaryAdmin(user)) return <Navigate to="/admin/dashboard" replace />;
  if (isApprovedSecondaryAdmin(user)) return <Navigate to="/admin/programs" replace />;
  return <Navigate to="/feed" replace />;
};

const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showFooter, setShowFooter] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const pageStartRef = useRef(Date.now());
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    const now = Date.now();
    const previousPath = prevPathRef.current;
    const durationMs = now - pageStartRef.current;

    if (previousPath && previousPath !== location.pathname) {
      trackPageDuration({
        path: previousPath,
        durationMs,
        user,
      });
    }

    trackPageView({
      path: location.pathname,
      title: document.title,
      user,
    });

    prevPathRef.current = location.pathname;
    pageStartRef.current = now;
  }, [location.pathname, user]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const now = Date.now();
      const path = prevPathRef.current;
      const durationMs = now - pageStartRef.current;

      if (path) {
        trackPageLeave({
          path,
          durationMs,
          user,
        });
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [user]);

  // Guards the arm-timer below against AuthContext's `user` object getting a
  // fresh reference on every one of the several sequential post-login
  // fetches (profile, communities, following, followers, follow-requests,
  // suggested users) — each of those used to re-run this effect and, because
  // the timer was cancelled by the previous run's cleanup, could kill the
  // pending "show the tour" timeout before it ever fired. Once armed, later
  // re-runs of the effect are no-ops, so the scheduled timer is never
  // cancelled by anything short of AppContent actually unmounting.
  const onboardingArmedRef = useRef(false);
  const onboardingTimerRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    // The tour only ever runs on /feed now (see OnboardingGuide's trimmed
    // STEPS list) — don't even arm it anywhere else, so it can't end up
    // mounted on a route it has nothing to point at.
    if (location.pathname !== '/feed') return;
    if (onboardingArmedRef.current) return;

    // A brand-new user typically lands on /feed (via player-interests) before
    // they've answered the cookie banner — it only just mounted, same as the
    // tour would. Checking cookie_consent once and giving up for good if it
    // wasn't set yet meant the tour silently never showed for that session
    // unless the user happened to leave /feed and come back after accepting/
    // declining. Poll briefly instead, so accepting the banner a few seconds
    // later still lets the tour arm right after, same page load.
    const tryArm = () => {
      if (onboardingArmedRef.current) return true;

      const userId = user._id || user.id || user.email;
      const onboardingDone = localStorage.getItem(`greenwich_onboarding_seen_${userId}`)
        || localStorage.getItem(`onboarding_done_${userId}`); // legacy key, still honored
      const shouldShow = localStorage.getItem('showOnboarding');
      const cameFromInterests = localStorage.getItem('completedPlayerInterests');
      // Cookie consent is a legal-priority bottom banner; never show the tour
      // (also anchored near the bottom on mobile) on top of / underneath it.
      const cookieConsentPending = !localStorage.getItem('cookie_consent');

      if (shouldShow !== 'true' || onboardingDone || cameFromInterests !== 'true') return true; // not applicable — stop polling
      if (cookieConsentPending) return false; // still waiting on the banner — keep polling

      onboardingArmedRef.current = true;
      localStorage.removeItem('showOnboarding');
      localStorage.removeItem('justRegistered');
      localStorage.removeItem('completedPlayerInterests');

      onboardingTimerRef.current = setTimeout(() => {
        setShowOnboarding(true);
      }, 800);
      return true;
    };

    if (tryArm()) return;
    // Give the user up to ~20s to respond to the cookie banner before giving
    // up for this page load (a later visit to /feed re-triggers this effect).
    let attempts = 0;
    const pollId = setInterval(() => {
      attempts += 1;
      if (tryArm() || attempts >= 40) clearInterval(pollId);
    }, 500);
    return () => clearInterval(pollId);
  }, [user, location.pathname]);

  // Only a genuine unmount of AppContent should cancel the pending timer.
  useEffect(() => () => { if (onboardingTimerRef.current) clearTimeout(onboardingTimerRef.current); }, []);

  // Manual re-entry point for the guide — the automatic tour above only ever
  // arms itself once, right after a brand-new player finishes registration,
  // so anyone who skipped it, missed it, or already had an account before
  // this feature shipped had no way to see it. Navbar's "How it works" item
  // dispatches this event; it jumps to /feed (the only page the tour points
  // at) and starts it, same as the automatic path, after a frame so the
  // page has mounted.
  useEffect(() => {
    const start = () => {
      if (location.pathname !== '/feed') { navigate('/feed'); }
      setTimeout(() => setShowOnboarding(true), location.pathname !== '/feed' ? 400 : 0);
    };
    window.addEventListener('greenwich:start-onboarding', start);
    return () => window.removeEventListener('greenwich:start-onboarding', start);
  }, [location.pathname, navigate]);

  const markOnboardingComplete = () => {
    setShowOnboarding(false);
    if (user) {
      const userId = user._id || user.id || user.email;
      localStorage.setItem(`greenwich_onboarding_seen_${userId}`, 'true');
      const token = localStorage.getItem('token');
      if (token) {
        fetch(`${GREENWICH_BACKEND_URL}/api/gamification/onboarding-complete`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => {});
      }
    }
  };

  const handleOnboardingComplete = markOnboardingComplete;
  const handleOnboardingSkip = markOnboardingComplete;

  // The tour only ever targets /feed — if a returning-mid-tour user
  // navigates elsewhere (navbar, back button, etc.) before finishing, don't
  // let the overlay follow them onto a page it has nothing to point at.
  useEffect(() => {
    if (showOnboarding && location.pathname !== '/feed') {
      setShowOnboarding(false);
    }
  }, [location.pathname, showOnboarding]);

  const resetOnboardingStatus = () => {
    if (user) {
      const userId = user._id || user.id || user.email;
      localStorage.removeItem(`greenwich_onboarding_seen_${userId}`);
      localStorage.removeItem(`onboarding_done_${userId}`);
      localStorage.removeItem('showOnboarding');
      localStorage.removeItem('justRegistered');
      localStorage.removeItem('completedPlayerInterests');
      window.location.reload();
    }
  };

  useEffect(() => {
    const noFooterPaths = [
      "/registration",
      "/registratioon",
      "/playerReg",
      "/player-interests",
      "/Reeeg",
      "/Reeg",
      "/Reg",
      "/Regnext"
    ];
    setShowFooter(!noFooterPaths.includes(location.pathname));
  }, [location.pathname]);

  return (
    <>
      <Navbar />

      <main>
        <Suspense fallback={
          <div className="loading" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <div className="spinner"></div>
          </div>
        }>
        <Routes>
          <Route path="/" element={<RoleBasedFeed />} />
          <Route path="/home" element={<HomePage />} />
          <Route path="/redirect" element={<RoleBasedRedirect />} />
          <Route path="/feed" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
          <Route path="/organizations" element={<ProtectedRoute><VerifiedOrganizations /></ProtectedRoute>} />
          {/* AccountProfile.jsx was a second, unreachable self-edit page (no link anywhere
              pointed here) that duplicated Dashboard.jsx; both routes now redirect there. */}
          <Route path="/account" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<RoleBasedDashboard />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/verified-organizations" element={<ProtectedRoute><VerifiedOrganizations /></ProtectedRoute>} />
          <Route path="/accountProfile" element={<Navigate to="/dashboard" replace />} />
          <Route path="/profile/:userId" element={<ProtectedRoute><UserPublicProfile /></ProtectedRoute>} />
          <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
          <Route path="/messages/dm/:conversationId" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
          <Route path="/messages/group/:id" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
          <Route path="/messages/dm/new" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
          <Route path="/messages/dm/new/:userId" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
          <Route path="/search" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />
          <Route path="/follow-requests" element={<ProtectedRoute><FollowRequestsPage /></ProtectedRoute>} />
          <Route path="/follow-settings" element={<ProtectedRoute><FollowSettings /></ProtectedRoute>} />
          <Route path="/blocked-users" element={<ProtectedRoute><BlockedUsers /></ProtectedRoute>} />
          <Route path="/muted-users" element={<ProtectedRoute><MutedUsers /></ProtectedRoute>} />
          <Route path="/active-sessions" element={<ProtectedRoute><ActiveSessions /></ProtectedRoute>} />
          <Route path="/notification-settings" element={<ProtectedRoute><NotificationSettings /></ProtectedRoute>} />
          <Route path="/security-settings" element={<ProtectedRoute><SecuritySettings /></ProtectedRoute>} />
          <Route path="/community" element={<ProtectedRoute><Community /></ProtectedRoute>} />
          <Route path="/my-communities" element={<ProtectedRoute><MyCommunities /></ProtectedRoute>} />
          <Route path="/community/group/:id" element={<ProtectedRoute><GroupView /></ProtectedRoute>} />
          <Route path="/community/group/:id/admin" element={<ProtectedRoute><GroupAdminPanel /></ProtectedRoute>} />
          <Route path="/community/group/:id/chat" element={<ProtectedRoute><GroupChat /></ProtectedRoute>} />
          <Route path="/community/join/:inviteCode" element={<ProtectedRoute><Navigate to="/community" /></ProtectedRoute>} />
          <Route path="/whoweare" element={<Whoweare />} />
          <Route path="/registration" element={<Registration />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/team-invite/:token" element={<TeamInviteConfirm />} />
          <Route path="/registratioon" element={<Registratioon />} />
          <Route path="/playerReg" element={<PlayerReg />} />
          <Route path="/Reeeg" element={<Reeeg />} />
          <Route path="/player-interests" element={<ProtectedRoute><PlayerInterest /></ProtectedRoute>} />
          <Route path="/programs" element={<Programs />} />
          <Route path="/programs/:slugId" element={<Programs />} />
          <Route path="/activities" element={<ProtectedRoute><Activities /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/Reg" element={<Reg />} />
          <Route path="/Regnext" element={<Regnext />} />
          <Route path="/Reeg" element={<Reeg />} />
          <Route path="/donate" element={<Donate />} />
          <Route path="/buddy" element={<ProtectedRoute><Buddy /></ProtectedRoute>} />
          <Route path="/levels" element={<LevelsPage />} />
          <Route path="/tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
          <Route path="/pro" element={<ProPage />} />
          <Route path="/payment/confirmation" element={<ProtectedRoute><PaymentConfirmation /></ProtectedRoute>} />
          <Route path="/shop" element={<ProtectedRoute><ShopPage /></ProtectedRoute>} />
          <Route path="/shop/history" element={<ProtectedRoute><ShopHistoryPage /></ProtectedRoute>} />
          <Route path="/greencoins" element={<ProtectedRoute><GreencoinsPage /></ProtectedRoute>} />
          <Route path="/programs/submit" element={<OrgRoute><OrgProgramSubmit /></OrgRoute>} />
          <Route path="/admin/programs-review" element={<PermissionRoute permission="programs_review"><AdminProgramsReview /></PermissionRoute>} />
          <Route path="/admin/dashboard" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
          <Route path="/admin/buddy-mentors" element={<AdminProtectedRoute><BuddyMentorApprovals /></AdminProtectedRoute>} />
          <Route path="/admin/approvals" element={<AdminProtectedRoute><PendingApprovals /></AdminProtectedRoute>} />
          <Route path="/admin/organizer-approvals" element={<PermissionRoute permission="organizer_approvals"><OrganizerApprovals /></PermissionRoute>} />
          <Route path="/organizer-approvals" element={<PermissionRoute permission="organizer_approvals"><OrganizerApprovals /></PermissionRoute>} />
          <Route path="/admin/users" element={<AdminProtectedRoute><AllUsers /></AdminProtectedRoute>} />
          <Route path="/admin/user-stats" element={<AdminProtectedRoute><UserStats /></AdminProtectedRoute>} />
          <Route path="/admin/user-segments" element={<AdminProtectedRoute><AdminUserSegments /></AdminProtectedRoute>} />
          <Route path="/admin/programs" element={<PermissionRoute permission="programs_review"><AdminPrograms /></PermissionRoute>} />
          <Route path="/admin/verified-organizations" element={<AdminProtectedRoute><VerifiedOrganizationsPage /></AdminProtectedRoute>} />
          <Route path="/admin/analytics" element={<AdminProtectedRoute><AdminAnalytics /></AdminProtectedRoute>} />
          <Route path="/admin/secondary-admins" element={<PrimaryAdminRoute><SecondaryAdmins /></PrimaryAdminRoute>} />
          <Route path="/admin/reports" element={<PermissionRoute permission="reports"><AdminReports /></PermissionRoute>} />
          <Route path="/admin/action-log" element={<AdminProtectedRoute><AdminActionLog /></AdminProtectedRoute>} />
          <Route path="/admin/inbox" element={<AdminProtectedRoute><AdminInbox /></AdminProtectedRoute>} />
          <Route path="/admin/investigate/:userId" element={<PermissionRoute permission="reports"><UserInvestigation /></PermissionRoute>} />
          <Route path="/admin/shop" element={<PermissionRoute permission="shop_economy"><AdminShop /></PermissionRoute>} />
          <Route path="/admin/tasks" element={<PermissionRoute permission="shop_economy"><AdminTasks /></PermissionRoute>} />
          <Route path="/organization/greencoins" element={<ProtectedRoute><OrganizationGreencoins /></ProtectedRoute>} />
          <Route path="/organization/panel" element={<OrgRoute><OrganizationPanel /></OrgRoute>} />
          <Route path="/admin/greencoins" element={<PermissionRoute permission="shop_economy"><AdminGreencoins /></PermissionRoute>} />
          <Route path="/admin/refunds" element={<PrimaryAdminRoute><AdminRefunds /></PrimaryAdminRoute>} />
          <Route path="/programs/my-submissions" element={<OrgRoute><OrgMySubmissions /></OrgRoute>} />
          <Route path="/organization-account-profile" element={<ProtectedRoute><OrganizationAccountProfile /></ProtectedRoute>} />
          <Route path="/organization-dashboard" element={<ProtectedRoute><OrganizationDashboard /></ProtectedRoute>} />
          <Route path="/organization-activities" element={<ProtectedRoute><OrganizationActivities /></ProtectedRoute>} />
          <Route path="/organization-settings" element={<ProtectedRoute><OrganizationSettings /></ProtectedRoute>} />
          <Route path="/organization/:id" element={<ProtectedRoute><OrganizationProfileRedirect /></ProtectedRoute>} />
          <Route path="/post/:postId" element={
            <ProtectedRoute>
              <SinglePostView />
            </ProtectedRoute>
          } />

          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        </Suspense>
      </main>

      {showFooter && <Footer />}
      <MessagesWidget />
      <CookieConsent />

      {/* ✅ Onboarding */}
      {showOnboarding && user && location.pathname === '/feed' && (
        <OnboardingGuide
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
        />
      )}

      {/* Dev mode reset */}
      {process.env.NODE_ENV === 'development' && user && (
        <button
          onClick={resetOnboardingStatus}
          style={{
            position: 'fixed', bottom: '20px', right: '20px', zIndex: 9999,
            padding: '8px 16px', background: '#ef4444', color: 'white',
            border: 'none', borderRadius: '8px', cursor: 'pointer',
            fontSize: '12px', opacity: 0.7, boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
          }}
          title="Reset onboarding (dev only)"
        >
          ↻ Reset Onboarding
        </button>
      )}
    </>
  );
};

function App() {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
    if (process.env.NODE_ENV === 'development') {
      console.log('🚀 App mounted in development mode');
    }
    document.body.classList.toggle('reduce-motion', localStorage.getItem('reduceMotion') === 'true');
  }, []);

  if (!isLoaded) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        height: '100vh', backgroundColor: '#f8fafc'
      }}>
        <div style={{
          width: '50px', height: '50px',
          aspectRatio: '1 / 1', boxSizing: 'border-box', flex: '0 0 auto',
          border: '4px solid #e5e7eb', borderTopColor: '#32614d',
          borderRadius: '50%', animation: 'spin 1s linear infinite'
        }}></div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <AuthProvider>
      <SocketProvider>
        <Router>
          <EnvironmentCheck />
          <AuthRedirect />
          <ToastContainer />
          <NotificationToast />
          <div className="app">
            <AppContent />
          </div>
        </Router>
      </SocketProvider>
    </AuthProvider>
  );
}

export default App;
